// API configuration utility
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  VERSION: process.env.REACT_APP_API_VERSION || 'v1',
  API_V1_STR: '/api/v1',
  
  getApiUrl: (endpoint) => {
    const baseUrl = API_CONFIG.BASE_URL;
    const version = API_CONFIG.VERSION;
    return `${baseUrl}/api/${version}${endpoint}`;
  },
  
  // Common API endpoints
  endpoints: {
    // Unified Authentication
    register: '/unified-auth/register',
    login: '/unified-auth/login',
    refreshToken: '/unified-auth/refresh-token',
    logout: '/auth/logout',
    me: '/unified-auth/me',
    switchRole: '/unified-auth/switch-role',
    updateProfile: '/auth/profile',
    verifyEmail: '/unified-auth/verify-email',
    resendVerification: '/unified-auth/resend-verification',
    setupPassword: '/auth/setup-password',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/unified-auth/reset-password',
    validateResetToken: '/unified-auth/validate-reset-token',
    
    // Legacy endpoints for backward compatibility
    registerCompany: '/auth/register',
    registerSubcontractor: '/auth/register-subcontractor',
    
    // Company Management
    getCompanies: '/companies',
    getCompany: '/companies',
    updateCompany: '/companies',
    getCompanyDashboardStats: '/companies/dashboard/stats',
    getCompanySubcontractors: '/companies/subcontractors',
    inviteCompanySubcontractor: '/companies/subcontractors/invite',
    updateCompanySubcontractor: '/companies/subcontractors',
    removeSubcontractor: '/companies/subcontractors',
    
    // Subcontractor Management
    getSubcontractors: '/subcontractors',
    getSubcontractor: '/subcontractors',
    updateSubcontractor: '/subcontractors',
    
    // Subcontractor Profile
    getSubcontractorProfile: '/subcontractors/profile',
    updateSubcontractorProfile: '/subcontractors/profile',
    changeSubcontractorPassword: '/subcontractors/change-password',
    getSubcontractorStats: '/subcontractors/stats',
    
    // Applications
    getApplications: '/applications',
    createApplication: '/applications',
    updateApplication: '/applications',
    
    // Reports
    generatePayslip: '/reports/payslip',
    generateBankExport: '/reports/bank-export',
    generateCISSummary: '/reports/cis-summary',
    
    // HMRC
    testHMRCConnection: '/unified-auth/hmrc/test-connection',
    storeHMRCCredentials: '/unified-auth/hmrc/credentials',
    submitCISToHMRC: '/unified-auth/hmrc/submit-cis',
    getHMRCSubmissions: '/unified-auth/hmrc/submissions',
    validateVAT: '/unified-auth/validate-vat',
    getOrganizationProfile: '/unified-auth/organization',
    updateOrganizationProfile: '/unified-auth/organization',
    testOrganizationHMRCConnection: '/unified-auth/organization/hmrc/test-connection',
    
    // Invites
    inviteSubcontractor: '/unified-auth/invite-subcontractor',
    validateInviteToken: '/unified-auth/invite/validate',
    
    // Connection Requests
    searchEmployers: '/unified-auth/search-employers',
    getEmployerContractTerms: '/unified-auth/employer',
    sendConnectionRequest: '/unified-auth/connection-request',
    acceptEmployerConnectionInvite: '/unified-auth/connection-invitation',
    getPendingRequests: '/unified-auth/pending-requests',
    acceptConnectionRequest: '/unified-auth/connection-request',
    rejectConnectionRequest: '/unified-auth/connection-request',
    revokeSubcontractorInvite: '/unified-auth/subcontractor-invite',
    
    // Payment Requests
    createPaymentRequest: '/unified-auth/payment-request',
    getPaymentRequests: '/unified-auth/payment-requests',
    getPaymentRequestStatusCounts: '/unified-auth/payment-requests/status-counts',
    approvePaymentRequest: '/unified-auth/payment-request',
    rejectPaymentRequest: '/unified-auth/payment-request',
    updatePaymentRequest: '/unified-auth/payment-request',
    withdrawPaymentRequest: '/unified-auth/payment-request',
    markPaymentAsPaid: '/unified-auth/payment-request',
    downloadPaymentPDF: '/unified-auth/payment-request',
    listMonthlyPaymentStatements: '/unified-auth/monthly-payment-statements',
    downloadMonthlyPaymentStatementsPDF: '/unified-auth/monthly-payment-statements/pdf',
    
    // Relationships (with pagination)
    getRelationships: '/unified-auth/relationships',

    profileBootstrapNames: '/unified-auth/profile/bootstrap-names',
    
    // Employer User Management
    createEmployerUser: '/unified-auth/employer-users',
    getEmployerUsers: '/unified-auth/employer-users',
    getEmployerUser: '/unified-auth/employer-users',
    updateEmployerUser: '/unified-auth/employer-users',
    deleteEmployerUser: '/unified-auth/employer-users',
    
    // Monthly CIS Statements
    getAvailableCISMonths: '/unified-auth/cis-months/available',
    getSubcontractorMonthlyStatement: '/unified-auth/monthly-statement/subcontractor',
    downloadMonthlyStatementPDF: '/unified-auth/monthly-statement/subcontractor',
    getCompanyCISMonthlySummary: '/unified-auth/monthly-summary/company',
    
    // Super Admin Endpoints
    getSuperAdminCompanies: '/unified-auth/super-admin/companies',
    getSuperAdminSubcontractors: '/unified-auth/super-admin/subcontractors',
    approveCompanyRegistration: '/unified-auth/super-admin/companies',
    rejectCompanyRegistration: '/unified-auth/super-admin/companies',
    getSuperAdminStats: '/unified-auth/super-admin/stats',
    getSuperAdminPayments: '/unified-auth/super-admin/payments',
    getSMTPSettings: '/unified-auth/super-admin/smtp-settings',
    updateSMTPSettings: '/unified-auth/super-admin/smtp-settings',
    testSMTP: '/unified-auth/super-admin/test-smtp',
    getSuperAdminMonthlyReturns: '/unified-auth/super-admin/monthly-returns',
    getSuperAdminEmployerMonthlySummary: '/unified-auth/super-admin/monthly-returns/employer'
  },
  
  // Helper function to get auth headers
  getAuthHeaders: () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  },

  // Decode JWT exp claim (seconds since epoch). Returns null on parse failure.
  getTokenExpMs: () => {
    const token = localStorage.getItem('access_token');
    if (!token || token.split('.').length < 2) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload || !payload.exp) return null;
      return Number(payload.exp) * 1000;
    } catch {
      return null;
    }
  },

  _refreshPromise: null,

  refreshAccessTokenIfNeeded: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Refresh when <= 10 minutes remain.
    const expMs = API_CONFIG.getTokenExpMs();
    if (!expMs) return;
    const remainingMs = expMs - Date.now();
    if (remainingMs > 10 * 60 * 1000) return;

    // Coalesce concurrent refresh attempts.
    if (API_CONFIG._refreshPromise) {
      await API_CONFIG._refreshPromise;
      return;
    }

    API_CONFIG._refreshPromise = (async () => {
      const response = await fetch(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.refreshToken),
        {
          method: 'POST',
          headers: API_CONFIG.getAuthHeaders()
        }
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data && data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        if (data.token_type) {
          localStorage.setItem('token_type', data.token_type);
        }
      }
    })();

    try {
      await API_CONFIG._refreshPromise;
    } finally {
      API_CONFIG._refreshPromise = null;
    }
  },

  // Helper function for authenticated requests with automatic token expiration handling
  fetchWithAuth: async (url, options = {}) => {
    try {
      // Sliding session: while user is active, refresh token shortly before expiry.
      await API_CONFIG.refreshAccessTokenIfNeeded();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...API_CONFIG.getAuthHeaders(),
          ...options.headers
        }
      });

      // Check for 401 Unauthorized (token expired)
      if (response.status === 401) {
        // Clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_type');
        localStorage.removeItem('user');
        
        // Show toast notification
        if (window.showToast) {
          window.showToast('Your session has expired. Please log in again.', 'error', 5000);
        }
        
        // Redirect to login page
        window.location.href = '/login';
        throw new Error('Token expired');
      }

      return response;
    } catch (error) {
      if (error.message === 'Token expired') {
        throw error; // Re-throw to prevent further processing
      }
      throw error;
    }
  }
};

export default API_CONFIG;
